import { formatUserFacingError, withErrorCode } from '@/lib/errors'
import { useState, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { parseEmailList, type ParsedEmailEntry } from '@/lib/csv'
import type { BulkAddResult } from '../../services/admin.mailingList.service'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  onBulkAdd: (entries: ParsedEmailEntry[]) => Promise<BulkAddResult>
}

export function BulkAddDialog({ open, onOpenChange, onBulkAdd }: Props) {
  const [rawText, setRawText] = useState('')
  const [saving, setSaving] = useState(false)
  const [result, setResult] = useState<BulkAddResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const parsed = useMemo(() => parseEmailList(rawText), [rawText])
  const validCount = parsed.filter((e) => e.isValid).length
  const invalidCount = parsed.filter((e) => !e.isValid).length

  async function handleImport() {
    if (validCount === 0) return
    setSaving(true)
    setError(null)
    try {
      const res = await onBulkAdd(parsed)
      setResult(res)
    } catch (err) {
      setError(formatUserFacingError(err, 'Import failed.'))
    } finally {
      setSaving(false)
    }
  }

  function handleClose() {
    setRawText('')
    setResult(null)
    setError(null)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) { handleClose() } else { onOpenChange(true) } }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Bulk Add Members</DialogTitle>
          <DialogDescription>
            Enter one email per line, or use{' '}
            <code className="text-xs">First Last &lt;email@example.com&gt;</code> format.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-3 py-2">
            <div className="flex gap-4 text-sm">
              <span className="text-green-600 dark:text-green-400">
                Added: <strong>{result.added}</strong>
              </span>
              <span className="text-muted-foreground">
                Skipped (already exists): <strong>{result.skipped}</strong>
              </span>
            </div>
            {result.errors.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-destructive">Errors:</p>
                <ul className="space-y-0.5 text-xs text-destructive">
                  {result.errors.map((e, i) => (
                    <li key={i}>{withErrorCode(e, 'mailing-list/import')}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="bulk-text">Email list</Label>
              <Textarea
                id="bulk-text"
                placeholder={`jane@example.com\nJohn Doe <john@example.com>\nbob@example.com`}
                rows={8}
                value={rawText}
                onChange={(e) => setRawText(e.target.value)}
              />
            </div>

            {parsed.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Badge variant="default">{validCount} valid</Badge>
                  {invalidCount > 0 && (
                    <Badge variant="destructive">{invalidCount} invalid</Badge>
                  )}
                </div>
                <div className="max-h-40 overflow-y-auto rounded-md border text-xs">
                  {parsed.map((entry, i) => (
                    <div
                      key={i}
                      className={`flex items-center gap-2 px-3 py-1.5 ${
                        !entry.isValid ? 'bg-destructive/10 text-destructive' : ''
                      }`}
                    >
                      <span className="flex-1 font-mono">{entry.email}</span>
                      {(entry.firstName || entry.lastName) && (
                        <span className="text-muted-foreground">
                          {[entry.firstName, entry.lastName].filter(Boolean).join(' ')}
                        </span>
                      )}
                      {!entry.isValid && <span className="text-destructive">✗ invalid</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose}>
            {result ? 'Close' : 'Cancel'}
          </Button>
          {!result && (
            <Button
              type="button"
              onClick={() => void handleImport()}
              disabled={saving || validCount === 0}
            >
              {saving ? 'Importing…' : `Import ${validCount} Members`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
